import { useQuery } from '@apollo/client';
import { IDropdownOption, Stack } from '@fluentui/react';
import {
  ComboboxFilter,
  DropdownFilter,
  FilterArrayType,
  FilterCombineType,
  FilterElement,
} from 'common/components/Filters';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import { SearchBar, SearchBarProps } from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import React, { useState } from 'react';
import { ApprovalSetupCommonData } from 'settings/account/approvalSetup/__generated__/ApprovalSetupCommonData';
import { useStyles } from './index.styles';
import { DEFAULT_FILTER } from '../utils';

import COMMON_DATA from '../../ApprovalSetupCommonData.graphql';

type ApprovalSetupSearchBarProps = Omit<SearchBarProps, 'searchEnabled'> & {
  onFilterChange: (filterArray: FilterArrayType[]) => void;
};

export const ApprovalSetupSearchBar: React.FC<ApprovalSetupSearchBarProps> = ({
  onFilterChange,
  ...props
}) => {
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([
    DEFAULT_FILTER,
  ]);
  const { data: commonData } = useQuery<ApprovalSetupCommonData>(COMMON_DATA);
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = useState<string | number | undefined>(
    'yes'
  );

  const statusTypeOptions = formatDropdownOptions(
    commonData?.invoiceStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
      includeAll: true,
    }
  );
  const approvalTypeOptions = formatDropdownOptions(
    commonData?.approvalTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.approvalType!,
      includeAll: true,
    }
  );
  const authorizedNodes = [
    { key: 'yes', text: 'Yes' },
    { key: 'no', text: 'No' },
  ];
  const authorizedOptions = formatDropdownOptions(authorizedNodes, {
    getKey: (item) => item.key,
    getText: (item) => item.text!,
    includeAll: true,
  });
  const handleChange = (
    event: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    if (option) {
      setSelectedKey(option.key);
      onOptionChange({
        combineType: FilterCombineType.AND,
        filterKey: 'isNewApprovalSelectable',
        label: option.text,
        value: option.key,
      });
    }
  };
  const onOptionChange = (filterData: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterData, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  const filters: FilterElement[] = [
    <ComboboxFilter
      options={approvalTypeOptions!}
      placeholder={'Type'}
      filterKey={'approvalTypeId'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'_statusTypeId'}
      options={statusTypeOptions}
      placeholder={'Status'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'isNewApprovalSelectable'}
      options={authorizedOptions}
      placeholder={'Authorized'}
      selectedKey={selectedKey}
      controlled
      onChange={handleChange}
    />,
  ];

  return (
    <SearchBar
      onFilterClear={() => {
        if (filterTypes.length) {
          onFilterChange([DEFAULT_FILTER]);
        }
        setFilterTypes([DEFAULT_FILTER]);
        setSelectedKey('yes');
      }}
      searchEnabled={false}
      filters={filters}
      {...props}
    />
  );
};
